// ─────────────────────────────────────────────────────────────────────────────
// Incidentes (orion.orion_errors) — lista, busca, associação e tempo real.
//
// A busca é HÍBRIDA: roda em paralelo uma busca semântica (embedding gerado no
// cliente pelo Gemini + RPC que ordena por similaridade) e uma busca literal
// (ilike nas colunas de texto). Os resultados são mesclados sem repetir.
//
// Por que as duas: a semântica acha "o que o usuário quis dizer" mas erra em
// termos exatos (nome de executável, número de ticket); a literal acerta o
// termo exato mas não entende sinônimo. Juntas cobrem os dois casos.
// ─────────────────────────────────────────────────────────────────────────────

import { supabase, tabela, tabelaDoSchema } from './supabase';
import { GEMINI_API_KEY, ORION_SCHEMA, WEBHOOKS } from '../config/ambiente';
import { validarTicket } from './mysuiteApi';

/** Quantos incidentes por página / por busca. */
export const INCIDENTES_POR_PAGINA = 20;

// ── Embedding ────────────────────────────────────────────────────────────────
//
// ONDE A CHAVE DO GEMINI DEVE FICAR.
//
// O 1.x gerava o embedding no próprio app, com a chave do Gemini escrita no
// renderer.js. Uma chave de API dentro de um app distribuído é cota aberta
// para quem a extrair — e no modo web bastaria abrir o bundle.
//
// O caminho preferido agora é o n8n: o app manda o texto para o webhook de
// busca, o servidor gera o embedding com a chave dele e devolve os incidentes.
// A chave nunca sai do servidor.
//
// A chamada direta ao Gemini continua aqui como saída de emergência, para
// quando o webhook não estiver disponível — e só funciona se alguém preencher
// REACT_APP_GEMINI_API_KEY de propósito, ciente do que isso significa.

/** true quando o embedding pode ser gerado no servidor (caminho preferido). */
export function embeddingNoServidor() {
  return !!WEBHOOKS.errosBuscar;
}

/**
 * Vetor de 3072 dimensões de um texto, via Gemini, chamado do próprio app.
 *
 * Usa o MESMO modelo com que os incidentes foram gravados
 * (gemini-embedding-001) — trocar o modelo invalida a comparação.
 */
export async function gerarEmbedding(texto) {
  if (!GEMINI_API_KEY) {
    throw new Error(
      'Busca semântica indisponível: configure REACT_APP_WEBHOOK_ERROS_BUSCAR '
      + '(recomendado, mantém a chave no servidor) ou REACT_APP_GEMINI_API_KEY.'
    );
  }

  const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-embedding-001:embedContent?key=${GEMINI_API_KEY}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'models/gemini-embedding-001',
      content: { parts: [{ text: texto }] },
      outputDimensionality: 3072,   // bate com a coluna embedding vector(3072)
    }),
  });

  if (!res.ok) throw new Error(`Gemini embedding retornou ${res.status}`);
  const data = await res.json();
  const values = data && data.embedding && data.embedding.values;
  if (!Array.isArray(values)) throw new Error('Resposta de embedding inválida.');
  return values;
}

// ── Normalização ─────────────────────────────────────────────────────────────

/**
 * Deixa no mesmo formato o que vem do n8n (Vector Store, embrulhado em
 * `document`) e o que vem direto do Supabase.
 */
export function normalizarIncidente(item) {
  if (item && item.document) {
    const m = item.document.metadata || {};
    return {
      id: m.id || null,
      erro: item.document.pageContent || '',
      executavel: m.executavel || null,
      executavel_data: m.executavel_data || null,
      sistema: m.sistema || null,
      detalhe: m.detalhe || null,
      solucao_paliativa: m.solucao_paliativa || null,
      status: m.status || 'pendente',
      numero_ticket: m.numero_ticket || null,
      operador_name: m.operador_name || null,
      created_at: m.created_at || null,
      tickets_associados: Array.isArray(m.tickets_associados) ? m.tickets_associados : [],
      imagens: Array.isArray(m.imagens) ? m.imagens : [],
      pdf_path: m.pdf_path || null,
      similarity: item.score || null,
    };
  }

  // Formato direto do Supabase: só garante que os arrays existam.
  if (item) {
    if (!Array.isArray(item.tickets_associados)) item.tickets_associados = [];
    if (!Array.isArray(item.imagens)) item.imagens = [];
  }
  return item;
}

// ── Filtros ──────────────────────────────────────────────────────────────────

/** true quando pelo menos um filtro está preenchido. */
export function temFiltroAtivo(filtros = {}) {
  return !!(filtros.status || filtros.sistema || filtros.operador
    || filtros.dataInicio || filtros.dataFim);
}

/** Aplica os filtros numa query do Supabase (usado na lista paginada). */
function aplicarFiltros(query, filtros = {}) {
  let q = query;
  if (filtros.status) q = q.eq('status', filtros.status);
  if (filtros.sistema) q = q.eq('sistema', filtros.sistema);
  if (filtros.operador) q = q.eq('operador_name', filtros.operador);
  if (filtros.dataInicio) q = q.gte('created_at', filtros.dataInicio);
  if (filtros.dataFim) q = q.lte('created_at', `${filtros.dataFim} 23:59:59`);
  return q;
}

/** Mesma checagem, mas no cliente — a busca semântica filtra depois da RPC. */
export function passaNosFiltros(incidente, filtros = {}) {
  if (filtros.status && incidente.status !== filtros.status) return false;
  if (filtros.sistema && incidente.sistema !== filtros.sistema) return false;
  if (filtros.operador && incidente.operador_name !== filtros.operador) return false;
  if (filtros.dataInicio || filtros.dataFim) {
    const d = (incidente.created_at || '').slice(0, 10);   // YYYY-MM-DD
    if (filtros.dataInicio && d < filtros.dataInicio) return false;
    if (filtros.dataFim && d > filtros.dataFim) return false;
  }
  return true;
}

/** Valores distintos de status, sistema e operador, para montar os selects. */
export async function opcoesDeFiltro() {
  const { data, error } = await tabela('orion_errors').select('status, operador_name, sistema');
  if (error) throw new Error(error.message);

  const distintos = (campo) => [...new Set((data || []).map(r => r[campo]).filter(Boolean))];

  return {
    status: distintos('status').sort(),
    operadores: distintos('operador_name').sort((a, b) => a.localeCompare(b, 'pt-BR')),
    sistemas: distintos('sistema').sort((a, b) => a.localeCompare(b, 'pt-BR')),
  };
}

/**
 * Executáveis já cadastrados, para o datalist do formulário.
 *
 * Existe para combater a duplicata por escrita: "GuardianNF 2.55",
 * "guardiannf2.55" e "Guardian NF 2.55" viram três executáveis distintos na
 * busca e nos filtros. Oferecer o que já existe faz o operador reaproveitar.
 */
export async function carregarExecutaveis() {
  const { data, error } = await tabela('orion_errors').select('executavel');
  if (error) return [];
  return [...new Set((data || []).map(r => r.executavel).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, 'pt-BR'));
}

/** Incidentes já gravados para um número de ticket — comparação exata. */
export async function incidentesDoTicket(numeroTicket) {
  const num = String(numeroTicket || '').replace(/\D/g, '');
  if (!num) return [];
  const { data, error } = await tabela('orion_errors')
    .select('*')
    .eq('numero_ticket', Number(num));
  if (error) return [];
  return (data || []).map(normalizarIncidente);
}

// ── Lista paginada ───────────────────────────────────────────────────────────

/**
 * Uma página de incidentes, mais recentes primeiro.
 * Devolve { lista, fimDaLista }.
 */
export async function listarIncidentes({ offset = 0, filtros = {} } = {}) {
  const inicio = offset;
  const fim = inicio + INCIDENTES_POR_PAGINA - 1;

  const { data, error } = await aplicarFiltros(tabela('orion_errors').select('*'), filtros)
    .order('created_at', { ascending: false })
    .range(inicio, fim);

  if (error) throw new Error(error.message);

  const lista = (data || []).map(normalizarIncidente);
  // Página menor que o tamanho pedido significa que acabou.
  return { lista, fimDaLista: lista.length < INCIDENTES_POR_PAGINA };
}

// ── Busca ────────────────────────────────────────────────────────────────────

/**
 * Busca semântica pelo n8n — caminho preferido.
 * O servidor gera o embedding e consulta o banco; a chave do Gemini não passa
 * pelo app. Devolve a lista já normalizada.
 */
async function buscarSemanticoNoServidor(termo, filtros) {
  const res = await fetch(WEBHOOKS.errosBuscar, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: termo,
      limite: temFiltroAtivo(filtros) ? INCIDENTES_POR_PAGINA * 5 : INCIDENTES_POR_PAGINA,
    }),
  });

  if (!res.ok) throw new Error(`Webhook de busca retornou ${res.status}`);

  const data = await res.json();
  let lista = (Array.isArray(data) ? data : data ? [data] : []).map(normalizarIncidente);
  if (temFiltroAtivo(filtros)) {
    lista = lista.filter(e => passaNosFiltros(e, filtros)).slice(0, INCIDENTES_POR_PAGINA);
  }
  return lista;
}

/** Busca semântica: embedding da pergunta + RPC ordenada por similaridade. */
async function buscarSemanticoNoApp(termo, filtros) {
  const embedding = await gerarEmbedding(termo);
  // Com filtros ativos pede mais candidatos, porque a RPC só ordena por
  // similaridade — quem filtra é o cliente, logo abaixo.
  const limite = temFiltroAtivo(filtros) ? INCIDENTES_POR_PAGINA * 5 : INCIDENTES_POR_PAGINA;

  const { data, error } = await supabase
    .schema(ORION_SCHEMA)
    .rpc('buscar_erros_similares', { query_embedding: embedding, limite });

  if (error) throw new Error(error.message);

  let lista = (Array.isArray(data) ? data : data ? [data] : []).map(normalizarIncidente);
  if (temFiltroAtivo(filtros)) {
    lista = lista.filter(e => passaNosFiltros(e, filtros)).slice(0, INCIDENTES_POR_PAGINA);
  }
  return lista;
}

/** Busca literal (ilike) nas colunas de texto. Sem embedding, sem RPC. */
async function buscarLiteral(termo, filtros) {
  // Vírgulas e parênteses quebram a sintaxe do .or() do PostgREST.
  const limpo = termo.replace(/[(),]/g, ' ').trim();
  if (!limpo) return [];

  const padrao = `%${limpo}%`;
  const condicoes = [
    `erro.ilike.${padrao}`,
    `detalhe.ilike.${padrao}`,
    `solucao_paliativa.ilike.${padrao}`,
    `executavel.ilike.${padrao}`,
    `sistema.ilike.${padrao}`,
  ];
  // numero_ticket é bigint: ilike não se aplica. Só casa exato se o termo for numérico.
  if (/^\d+$/.test(limpo)) condicoes.push(`numero_ticket.eq.${limpo}`);

  const { data, error } = await aplicarFiltros(tabela('orion_errors').select('*'), filtros)
    .or(condicoes.join(','))
    .order('created_at', { ascending: false })
    .limit(INCIDENTES_POR_PAGINA);

  if (error) throw new Error(error.message);
  return (data || []).map(normalizarIncidente);
}

/**
 * Busca híbrida. Devolve { lista, erro }.
 *
 * As duas buscas rodam em paralelo e uma não derruba a outra: se a semântica
 * falhar (Gemini fora, RPC ausente), a literal ainda responde. `erro` só vem
 * preenchido quando AS DUAS falham.
 *
 * Ordem do resultado: os semânticos primeiro (trazem o % de similaridade), e
 * os que só a literal achou vão no fim, sem percentual.
 *
 * NOTA DE MIGRAÇÃO: na versão 1.x esta função tinha a condição de guarda
 * invertida (`if (cli)` no lugar de `if (!cli)` — renderer.js:5825), o que
 * fazia a busca abortar com "Supabase não configurado" justamente quando o
 * Supabase ESTAVA configurado. Aqui a guarda simplesmente não é mais
 * necessária: o cliente é criado na carga do módulo.
 */
export async function buscarIncidentes(termo, filtros = {}) {
  const query = (termo || '').trim();
  if (!query) return { lista: [], erro: null };

  // Semântica pelo servidor quando o webhook existe; senão, pelo app.
  const semantica = embeddingNoServidor()
    ? buscarSemanticoNoServidor(query, filtros)
    : buscarSemanticoNoApp(query, filtros);

  const [resSem, resLit] = await Promise.allSettled([
    semantica,
    buscarLiteral(query, filtros),
  ]);

  const semanticos = resSem.status === 'fulfilled' ? resSem.value : [];
  const literais = resLit.status === 'fulfilled' ? resLit.value : [];

  // Mescla por id, sem repetir.
  const porId = new Map();
  semanticos.forEach(e => { if (e && e.id) porId.set(e.id, e); });
  literais.forEach(e => { if (e && e.id && !porId.has(e.id)) porId.set(e.id, e); });
  const lista = Array.from(porId.values());

  const erro = (!lista.length && resSem.status === 'rejected' && resLit.status === 'rejected')
    ? (resSem.reason?.message || 'Falha na busca')
    : null;

  return { lista, erro };
}

// ── Tickets associados ───────────────────────────────────────────────────────

/**
 * Grava o array completo de tickets_associados.
 *
 * O `.select()` no fim não é enfeite: sem policy de UPDATE, o Supabase devolve
 * { data: [], error: null } e o cliente acha que gravou. Conferir que voltou
 * alguma linha é a única forma de detectar o bloqueio silencioso do RLS.
 */
export async function salvarTicketsAssociados(numeroTicketIncidente, novoArray) {
  // numero_ticket é INT no banco — garante o tipo no filtro.
  const ticketInt = parseInt(numeroTicketIncidente, 10);

  const { data, error } = await tabela('orion_errors')
    .update({ tickets_associados: novoArray })
    .eq('numero_ticket', ticketInt)
    .select('numero_ticket, tickets_associados');

  if (error) return { ok: false, error: error.message };
  if (!data || data.length === 0) {
    return { ok: false, error: 'Nada foi gravado (provável bloqueio de RLS — falta policy de UPDATE).' };
  }
  return { ok: true };
}

/**
 * Associa um ticket ao incidente. Devolve a mensagem de erro, ou null se deu certo.
 * Valida no MySuite antes: ticket inexistente não entra.
 */
export async function associarTicket(incidente, ticketBruto) {
  const novo = parseInt(String(ticketBruto).trim(), 10);
  if (!novo || novo <= 0) return 'Informe um número de ticket válido.';
  if (novo === incidente.numero_ticket) return 'O ticket do próprio incidente não pode ser associado a si mesmo.';

  const atual = incidente.tickets_associados || [];
  if (atual.includes(novo)) return `O ticket ${novo} já está associado.`;

  const val = await validarTicket(novo);
  if (!val.valido) return val.mensagem;

  const novoArray = [...atual, novo].sort((a, b) => a - b);
  const res = await salvarTicketsAssociados(incidente.numero_ticket, novoArray);
  if (!res.ok) return `Falha ao salvar: ${res.error}`;

  incidente.tickets_associados = novoArray;
  return null;
}

/** Remove um ticket da lista. Devolve mensagem de erro, ou null. */
export async function desassociarTicket(incidente, ticketRemovido) {
  const atual = incidente.tickets_associados || [];
  const novoArray = atual.filter(t => t !== ticketRemovido);

  const res = await salvarTicketsAssociados(incidente.numero_ticket, novoArray);
  if (!res.ok) return `Falha ao salvar: ${res.error}`;

  incidente.tickets_associados = novoArray;
  return null;
}

// ── Tempo real ───────────────────────────────────────────────────────────────

let canalRealtime = null;

/**
 * Assina INSERT e UPDATE em orion_errors.
 * Ligado já no login (não só ao abrir a aba), para o operador ser avisado de
 * mudança de status mesmo trabalhando em outra tela.
 * Devolve a função que cancela a assinatura.
 */
export function assinarIncidentes({ aoInserir, aoAtualizar }) {
  if (canalRealtime) return () => {};   // uma assinatura só por sessão

  try {
    canalRealtime = supabase
      .channel('orion_errors_rt')
      .on('postgres_changes',
        { event: 'INSERT', schema: ORION_SCHEMA, table: 'orion_errors' },
        payload => aoInserir && aoInserir(normalizarIncidente(payload.new)))
      .on('postgres_changes',
        { event: 'UPDATE', schema: ORION_SCHEMA, table: 'orion_errors' },
        payload => aoAtualizar && aoAtualizar(normalizarIncidente(payload.new), payload.old))
      .subscribe();
  } catch (e) {
    console.warn('[Orion] Realtime de incidentes:', e.message);
    return () => {};
  }

  return () => {
    try { supabase.removeChannel(canalRealtime); } catch (e) {}
    canalRealtime = null;
  };
}

// ── Gravação via n8n ─────────────────────────────────────────────────────────

/**
 * Pede ao n8n que extraia os campos do incidente a partir do texto do
 * atendimento.
 *
 * As sete chaves abaixo são as do 1.4.1, com estes nomes: o texto formalizado
 * vai em `ticket_texto` (e não `texto_final`), e `tipo_atendimento` é fixo em
 * "Setor de sistemas" — é o que o fluxo lê para saber de onde veio.
 */
export async function extrairIncidenteViaN8n(solicitante, anotacoes, textoFinal, numeroTicket, operador) {
  if (!WEBHOOKS.erros) throw new Error('Webhook de incidentes não configurado.');

  const res = await fetch(WEBHOOKS.erros, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      solicitante,
      anotacoes,
      ticket_texto: textoFinal,
      numero_ticket: numeroTicket,
      tipo_atendimento: 'Setor de sistemas',
      operador_nome: operador?.nome || '',
      operador_email: operador?.email || '',
    }),
  });

  if (!res.ok) throw new Error(`Webhook de incidentes retornou ${res.status}`);
  const dados = await res.json();
  // O n8n devolve ora o objeto, ora um array de um item — o 1.4.1 desembrulha.
  return Array.isArray(dados) ? dados[0] : dados;
}

/**
 * Nome(s) da empresa a partir do CNPJ, em `pulse.empresas_config`.
 *
 * Acompanha o incidente no campo `nome_empresa` — o fluxo do n8n grava isso e
 * é por ele que o acervo fica pesquisável por cliente. Vazio quando não achar:
 * um incidente sem o nome é melhor do que um incidente não registrado.
 */
export async function nomeEmpresaPorCnpj(cnpj) {
  const c = String(cnpj || '').trim();
  if (!c) return '';
  try {
    const { data, error } = await tabelaDoSchema('pulse', 'empresas_config')
      .select('nome_empresa, razao_social')
      .eq('cnpj', c);
    if (error || !data || !data.length) return '';
    const nomes = data.map(r => (r.nome_empresa || r.razao_social || '').trim()).filter(Boolean);
    return [...new Set(nomes)].join(' / ');
  } catch (err) {
    console.warn('[Orion] Falha ao buscar nome da empresa por CNPJ:', err.message);
    return '';
  }
}

/** Salva o incidente pelo n8n (que grava no banco e gera o embedding). */
export async function salvarIncidenteViaN8n(payload) {
  if (!WEBHOOKS.errosSalvar) throw new Error('Webhook de gravação de incidentes não configurado.');

  const res = await fetch(WEBHOOKS.errosSalvar, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) throw new Error(`Webhook de gravação retornou ${res.status}`);
  return await res.json();
}

/** Atualiza campos de um incidente já gravado (imagens, pdf_path, correções). */
export async function atualizarIncidente(numeroTicket, campos) {
  const ticketInt = parseInt(numeroTicket, 10);
  const { data, error } = await tabela('orion_errors')
    .update(campos)
    .eq('numero_ticket', ticketInt)
    .select('numero_ticket');

  if (error) return { ok: false, error: error.message };
  if (!data || !data.length) {
    return { ok: false, error: 'Nada foi gravado (provável bloqueio de RLS — falta policy de UPDATE).' };
  }
  return { ok: true };
}

// ─────────────────────────────────────────────────────────────────────────────
// A CORREÇÃO VOLTOU PARA O OPERADOR
//
// Fim do caminho de uma demanda escalonada: o setor de Sistemas corrigiu, o
// suporte avançado devolveu o chamado ao operador que o abriu (é o Pulse que
// faz isso, gravando `devolvido_operador_em`), e agora é ele quem agenda a
// atualização com o cliente.
//
// O chamado chega pela fila de retorno do MySuite, como qualquer agendamento —
// o que estas funções acrescentam é o PORQUÊ: este não é um retorno comum, é
// uma correção esperando para ser aplicada. E, quando o operador conclui o
// atendimento, é por aqui que o incidente é encerrado do outro lado, sem ele
// precisar abrir o Pulse.
// ─────────────────────────────────────────────────────────────────────────────

/** Os status que significam "já encerrado" em orion_errors. */
const STATUS_ENCERRADOS = ['concluido', 'concluido_sistemas', 'excluido'];

/** true quando o incidente saiu do fluxo — concluído aqui, pelo setor, ou excluído. */
export function incidenteEncerrado(incidente) {
  return STATUS_ENCERRADOS.includes(String(incidente?.status || ''));
}

/**
 * Os incidentes DESTE ticket que voltaram do setor de Sistemas e seguem abertos.
 *
 * A regra mora aqui, e não na tela, porque duas telas perguntam a mesma coisa —
 * a formalização, para avisar o operador, e a conclusão, para encerrar.
 */
export async function retornosDoTicket(numeroTicket) {
  const lista = await incidentesDoTicket(numeroTicket);
  return lista.filter(i => i.devolvido_operador_em && !incidenteEncerrado(i));
}

/**
 * Os incidentes que voltaram do setor de Sistemas para ESTE operador e ainda
 * não foram concluídos.
 *
 * Filtra por `operador_email` — quem registrou o incidente é quem atendeu o
 * cliente, e é a ele que o chamado volta (ver a rota devolver-operador, no
 * Pulse). Sem e-mail não há como saber de quem é: devolve lista vazia em vez de
 * mostrar o retorno de todo mundo.
 */
export async function incidentesDevolvidosDoOperador(email) {
  const alvo = String(email || '').trim().toLowerCase();
  if (!alvo) return [];
  const { data, error } = await tabela('orion_errors')
    .select('id, numero_ticket, erro, sistema, nexus_versao, status, devolvido_operador_em, devolvido_operador_por')
    .eq('operador_email', alvo)
    .not('devolvido_operador_em', 'is', null)
    .not('status', 'in', `(${STATUS_ENCERRADOS.join(',')})`)
    .order('devolvido_operador_em', { ascending: false });
  if (error) {
    console.warn('[Orion] Retornos do setor de Sistemas:', error.message);
    return [];
  }
  return data || [];
}

/**
 * Conclui o incidente de um ticket que voltou do setor de Sistemas.
 *
 * Chamada quando o operador fecha o atendimento no Orion: é esse o momento em
 * que a demanda termina de verdade — o cliente foi atualizado e confirmou. Até
 * aqui o Pulse ficava esperando alguém marcar isso à mão de lá, e a solicitação
 * do Nexus seguia aberta em "aguardando solicitante".
 *
 * Fecha também a solicitação no Nexus, mas não daqui: um gatilho do banco
 * reage à mudança de status e chama `nexus.concluir_solicitacao`
 * (47_retorno_operador.sql). O Orion não precisa conhecer o Nexus.
 *
 * SÓ MEXE NO QUE FOI DEVOLVIDO. Um incidente que ainda está sendo corrigido não
 * é concluído porque o operador fechou o chamado — pode ser outro assunto no
 * mesmo ticket, e encerrar a demanda do setor por tabela seria perder trabalho.
 *
 * `solucao` só é gravada se o campo estiver vazio: o que o suporte avançado
 * escreveu como solução definitiva vale mais do que o texto do atendimento.
 *
 * Devolve `{ concluidos, falhas }`. Nunca lança: a conclusão do ticket no
 * MySuite já aconteceu quando isto roda, e uma falha aqui não pode parecer que
 * o atendimento não foi enviado.
 */
export async function concluirIncidenteDoTicket(numeroTicket, { operadorNome, solucao } = {}) {
  const num = String(numeroTicket || '').replace(/\D/g, '');
  if (!num) return { concluidos: 0, falhas: 0 };

  const { data, error } = await tabela('orion_errors')
    .select('id, numero_ticket, status, devolvido_operador_em, solucao_definitiva')
    .eq('numero_ticket', Number(num))
    .not('devolvido_operador_em', 'is', null);
  if (error) {
    console.warn('[Orion] Conclusão do incidente:', error.message);
    return { concluidos: 0, falhas: 0 };
  }

  const pendentes = (data || []).filter(i => !STATUS_ENCERRADOS.includes(String(i.status || '')));
  if (!pendentes.length) return { concluidos: 0, falhas: 0 };

  const agora = new Date().toISOString();
  const texto = String(solucao || '').trim();
  let concluidos = 0;
  let falhas = 0;

  for (const inc of pendentes) {
    const campos = {
      status: 'concluido',
      data_resolucao: agora,
      status_atualizado_em: agora,
      status_atualizado_por: operadorNome || 'Operador',
      status_observacao: 'Concluído pelo operador no Orion, após a atualização no cliente.',
    };
    if (texto && !String(inc.solucao_definitiva || '').trim()) {
      campos.solucao_definitiva = texto;
    }
    const { error: errUp } = await tabela('orion_errors').update(campos).eq('id', inc.id);
    if (errUp) {
      console.warn(`[Orion] Incidente ${inc.id} não foi concluído:`, errUp.message);
      falhas += 1;
    } else {
      concluidos += 1;
    }
  }
  return { concluidos, falhas };
}

/** true quando o incidente já foi concluído (não aceita mais edição de fluxo). */
export function incidenteConcluido(incidente) {
  const s = String(incidente?.status || '').toLowerCase();
  return s === 'concluido' || s === 'concluído' || s === 'resolvido';
}

/** Rótulo amigável do status. */
export function rotuloStatus(status) {
  const mapa = {
    pendente: 'Pendente',
    em_analise: 'Em análise',
    'em análise': 'Em análise',
    concluido: 'Concluído',
    'concluído': 'Concluído',
    resolvido: 'Resolvido',
    cancelado: 'Cancelado',
  };
  return mapa[String(status || '').toLowerCase()] || (status || '—');
}
